import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  SearchPhoneFraudForm,
  SearchEmailFraudForm,
  SearchURLFraudForm,
  SearchIpFraudForm,
  FcraWarning,
  SearchFormProps,
} from '@/reports/index';
import {
  Box,
  Button,
  styled,
  ButtonProps,
  Grid,
  Modal,
  Text,
  useMediaQuery,
  useTheme,
} from '@/theme/index';

const SearchForms = styled('div')(({ theme }) => ({
  overflow: 'hidden',
  maxHeight: '600px',
  padding: theme.spacing(1, 0),
}));

const SearchTab = styled((props: ButtonProps) => (
  <Button color="success" size="small" {...props} />
))(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(0.5),

  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.body2.fontSize,

    '& .search-label-caption': {
      display: 'block',
      fontSize: '0.6rem',
      fontWeight: theme.typography.caption.fontWeight,
      lineHeight: theme.typography.caption.lineHeight,
    },
  },

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

type TabType = 'phone' | 'email' | 'ip' | 'url';

export interface FraudSearchContainerProps {
  excludeTabs?: TabType[];
  initialTab?: TabType;
  onSearchChange?: (tab: TabType) => void;
  onSearchSuccess?: SearchFormProps['onSuccess'];
  onSearchError?: SearchFormProps['onError'];
  displayBulkUpload?: boolean;
}

export function FraudSearchContainer({
  excludeTabs = [],
  initialTab = 'phone',
  onSearchChange,
  onSearchSuccess,
  onSearchError,
  displayBulkUpload = false,
}: FraudSearchContainerProps) {
  const queryContext = useQueryClient();
  const theme = useTheme();
  const { trackEventGA4 } = useAppConfigContext();
  const {
    links: { tosLink, fcraLink },
    data: { brandName },
  } = useAppConstantsContext();
  const isBellowMedium = useMediaQuery(theme.breakpoints.down('md'));
  const [currentTab, setCurrentTab] = React.useState<TabType>(initialTab);
  const [open, setOpen] = React.useState(false);
  const handleTabChange = (tab: TabType) => {
    setCurrentTab(tab);

    if (onSearchChange) {
      onSearchChange(tab);
    }
  };

  const handleSearchSuccess: SearchFormProps['onSuccess'] = (searchPayload) => {
    if (onSearchSuccess) {
      onSearchSuccess(searchPayload);
    }
  };

  const handleBulkUploadClick = () => {
    trackEventGA4({
      event_name: 'feature_engagement',
      type: 'fraud_bulk_interest',
      interacted_from: 'fraud',
    });
    setOpen(true);
  };

  const handleSearchError: SearchFormProps['onError'] = ((
    error: unknown,
    variables: unknown,
    context: unknown
  ) => {
    queryContext
      ?.getDefaultOptions()
      ?.mutations?.onError?.(error, variables, context);

    if (onSearchError) {
      onSearchError(error as Error);
    }
  }) as SearchFormProps['onError'];

  const formComponents = {
    phone: SearchPhoneFraudForm,
    email: SearchEmailFraudForm,
    ip: SearchIpFraudForm,
    url: SearchURLFraudForm,
  };

  const currentForm = () => {
    const FormComponent = formComponents[currentTab];
    return FormComponent ? (
      <FormComponent
        onSuccess={handleSearchSuccess}
        onError={handleSearchError}
      />
    ) : null;
  };

  const fraudSearches: { tab: TabType; label: JSX.Element }[] = [
    {
      tab: 'phone',
      label: (
        <>
          Phone <span className="search-label-caption">Verification</span>
        </>
      ),
    },
    {
      tab: 'email',
      label: (
        <>
          Email <span className="search-label-caption">Verification</span>
        </>
      ),
    },
    {
      tab: 'ip',
      label: (
        <>
          IP <span className="search-label-caption">Lookup</span>
        </>
      ),
    },
    {
      tab: 'url',
      label: (
        <>
          URL <span className="search-label-caption">Scanning</span>
        </>
      ),
    },
  ];
  const activeSearches = fraudSearches.filter(
    (tab) => !excludeTabs.includes(tab.tab)
  );

  return (
    <SearchForms data-cy="fraud-search-container">
      <Grid container spacing={{ xs: 0.5, sm: 1 }} mt={7} pb={2}>
        {activeSearches.map(({ tab, label }) => (
          <Grid item key={tab}>
            <SearchTab
              data-cy={`fraud-search-container-${tab}-tab`}
              onClick={() => handleTabChange(tab)}
              variant={currentTab === tab ? 'contained' : 'outlined'}
              size={isBellowMedium ? 'small' : 'medium'}
            >
              {label}
            </SearchTab>
          </Grid>
        ))}
      </Grid>
      <Box
        sx={(thm) => ({
          marginTop: thm.spacing(3),
          marginBottom: thm.spacing(2),
          backgroundColor: thm.palette.background.default,
          borderRadius: '5px',
        })}
      >
        {currentForm()}
      </Box>
      {displayBulkUpload && (
        <Button
          sx={{ display: 'block' }}
          onClick={handleBulkUploadClick}
          variant="text"
          data-cy="fraud-search-container-bulk-upload-button"
        >
          Have a list? Bulk Upload
        </Button>
      )}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        hasCloseIcon
      >
        <Box p={4}>
          <Text
            data-cy="fraud-search-container-modal-title"
            textAlign="center"
            variant="h3"
          >
            Thank you for your interest!
          </Text>
          <Text textAlign="center" variant="body1">
            This feature is coming soon.
          </Text>
        </Box>
      </Modal>
      <FcraWarning
        fcraLink={fcraLink}
        tosLink={tosLink}
        brandName={brandName}
      />
    </SearchForms>
  );
}
